import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "buttons-container" }

import ResumeBase from "@/Layouts/ResumeBase/ResumeBase.vue";
import TextAreaInput from "@/components/Common/Inputs/TextAreaInput/TextAreaInput.vue";
import AppButton from "@/components/Common/AppButton/AppButton.vue";
import { useResumeStore, useErrorStore } from "@/store";
import { onMounted, onUnmounted, ref, computed } from "vue";
import { useRouter, onBeforeRouteLeave, useRoute } from "vue-router";
import { onIonViewDidEnter } from "@ionic/vue";
import { handleBeforeUnload, focusOnMainContent } from "@/helpers";
import {
  IonTextareaCustomEvent,
  TextareaChangeEventDetail,
  IonInputCustomEvent,
} from "@ionic/core/components";
import { useI18n } from "vue-i18n";
import { Resume } from "@/store/models/resume.models";

export default /*@__PURE__*/_defineComponent({
  __name: 'ResumePersonalStatement',
  setup(__props) {

const { t } = useI18n<{}>({
  useScope: "global",
});

const resumeStore = useResumeStore();
const errorStore = useErrorStore();
const router = useRouter();
const route = useRoute();

const skipOnRouteBeforeLeave = ref(false);

onMounted(async () => {
  if (!resumeStore.resumeId) {
    await resumeStore.requestResume();
    if (!resumeStore.resumeId) {
      await resumeStore.createResume();
    }
  }
  window.addEventListener("resize", handleResize);
  handleResize();
  initValidationMap();
});

onUnmounted(async () => {
  window.removeEventListener("resize", handleResize);
});

onBeforeRouteLeave(() => {
  window.removeEventListener("beforeunload", handleBeforeUnload);
});
onIonViewDidEnter(() => {
  focusOnMainContent(route?.meta?.mainContentId);
  skipOnRouteBeforeLeave.value = false;
  window.addEventListener("beforeunload", handleBeforeUnload);
});

const autogrowEnabled = ref(false);
const handleResize = () => {
  const textHeight =
    document.querySelector<HTMLElement>("#text-area-input .textarea-input")
      ?.offsetHeight || 0;

  if (window.innerWidth <= 769 && textHeight >= 344) {
    autogrowEnabled.value = false;
  } else autogrowEnabled.value = true;
};

const textareaHeight = computed(() => {
  if (window.innerWidth > 769) {
    return 250;
  } else {
    return 152;
  }
});

const isFormSaving = ref(false);

const onInputChange = (
  event: IonTextareaCustomEvent<TextareaChangeEventDetail>
) => {
  if (event.detail?.value === undefined || event.detail?.value === null) return;
  resumeStore.setPersonalStatementValue(event.detail.value);
  if (autogrowEnabled.value) {
    handleResize();
  }
};

const saveForm = async () => {
  skipOnRouteBeforeLeave.value = true;
  errorStore.setSaveFormError(false);
  try {
    isFormSaving.value = true;
    await resumeStore.updatePersonalStatement();
    router.push({
      name: "ResumeOverview",
    });
  } catch (error) {
    console.error("An error occured saving the form: ", error);
    errorStore.setSaveFormError(true);
  } finally {
    isFormSaving.value = false;
  }
};
const returnToPage = () => {
  router.push({
    name: "ResumeOverview",
  });
};

/* VALIDATION */

const rules = {
  personalStatement: {
    invalidCharacters: t(
      "resume.personalStatementForm.personalStatement.invalidCharactersError"
    ),
    characterLimit: t(
      "resume.personalStatementForm.personalStatement.characterLimitError"
    ),
  },
};

const validationMap = ref<Map<keyof Resume, boolean>>(new Map([]));
const initValidationMap = () => {
  validationMap.value.set("personalStatement", true);
};
const errorMessageMap = ref<Map<keyof Resume, string>>(
  new Map([["personalStatement", ""]])
);

const isFormSavingEnabled = computed(() => {
  for (let isValid of validationMap.value.values()) {
    if (!isValid) return false;
  }
  return true;
});

const validatePersonalStatement = (event: IonInputCustomEvent<FocusEvent>) => {
  const itemEl = event.target.parentElement;
  itemEl?.classList.remove("ion-invalid");
  const personalStatement = event.target.value as string;
  if (personalStatement && personalStatement.length >= 1200) {
    itemEl?.classList.add("ion-invalid");
    errorMessageMap.value.set(
      "personalStatement",
      rules.personalStatement.characterLimit
    );
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ResumeBase, {
    breadcrumbs: [
      {
        route: 'personal-statement',
        label: _ctx.$t('resume.breadcrumbs.personalStatement'),
      },
    ],
    mainContentId: "resume-personal-statement-main-content",
    formSaveRetryFunction: saveForm,
    skipOnRouteBeforeLeave: skipOnRouteBeforeLeave.value
  }, {
    title: _withCtx(() => [
      _createTextVNode(_toDisplayString(_ctx.$t("resume.personalStatementForm.title")), 1)
    ]),
    description: _withCtx(() => [
      _createTextVNode(_toDisplayString(_ctx.$t("resume.personalStatementForm.description")), 1)
    ]),
    "main-content": _withCtx(() => [
      _createElementVNode("form", {
        "aria-label": _unref(t)('resume.personalStatementForm.title')
      }, [
        _createVNode(TextAreaInput, {
          value: _unref(resumeStore).resumePersonalStatement || '',
          labelText: 
            _ctx.$t('resume.personalStatementForm.personalStatement.label')
          ,
          placeholderText: 
            _ctx.$t('resume.personalStatementForm.personalStatement.placeholder')
          ,
          errorText: errorMessageMap.value.get('personalStatement'),
          autogrow: autogrowEnabled.value,
          startingHeight: textareaHeight.value,
          onOnChange: _cache[0] || (_cache[0] = 
            (event) => {
              onInputChange(event);
              validatePersonalStatement(event);
            }
          ),
          isLoading: _unref(resumeStore).isResumeLoading,
          id: "text-area-input",
          "data-test": "text-area-input"
        }, null, 8, ["value", "labelText", "placeholderText", "errorText", "autogrow", "startingHeight", "isLoading"]),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(AppButton, {
            size: "default",
            type: "primary",
            label: _ctx.$t('resume.personalStatementForm.saveButtonText'),
            ariaLabel: _ctx.$t('resume.personalStatementForm.saveButtonText'),
            onClick: saveForm,
            "data-test": "save-form-btn",
            class: "save-form-btn",
            width: "100%",
            isLoading: isFormSaving.value,
            disabled: !isFormSavingEnabled.value
          }, null, 8, ["label", "ariaLabel", "isLoading", "disabled"]),
          _createVNode(AppButton, {
            size: "default",
            type: "textOnly",
            label: _unref(t)('resume.personalStatementForm.closeButtonText'),
            ariaLabel: _unref(t)('resume.personalStatementForm.closeButtonText'),
            "data-test": "close-form-btn",
            class: "close-form-btn",
            id: "closeBtn",
            onClick: returnToPage,
            width: "100%"
          }, null, 8, ["label", "ariaLabel"])
        ])
      ], 8, _hoisted_1)
    ]),
    "resume-helper": _withCtx(() => [...(_cache[1] || (_cache[1] = []))]),
    _: 1
  }, 8, ["breadcrumbs", "skipOnRouteBeforeLeave"]))
}
}

})